import React, {
    useMemo,
    useState,
} from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    InputGroup,
    Row,
} from "react-bootstrap";
import { useFormikContext } from "formik";
import SmartSearchSelect from "../../../components/Smart/SmartSearchSelect";
import HelpTooltip from "./HelpTooltip";
import {
    COUNTRY_EXTRA_CONFIG,
} from "../utils/countryExtras";

const decimalOrEmpty = (raw) => {
    if (raw === "") {
        return "";
    }

    const parsed = Number(raw);

    return Number.isFinite(parsed)
        ? parsed
        : "";
};

const normalizeCountryRows = (rows) =>
    Array.isArray(rows)
        ? rows
        : [];

export default function CountryExtraCard({
    serviceKey,
    countries,
}) {
    const {
        values,
        setFieldValue,
        submitCount,
    } = useFormikContext();

    const [selectedCountryIds, setSelectedCountryIds] =
        useState([]);

    const config =
        COUNTRY_EXTRA_CONFIG[serviceKey];

    const service =
        values.countryServices?.[serviceKey] || {
            enabled: false,
            countries: [],
        };

    const rows =
        normalizeCountryRows(
            service.countries
        );

    const basePath =
        `countryServices.${serviceKey}`;

    const countryById = useMemo(
        () =>
            new Map(
                (countries || []).map(
                    (country) => [
                        String(country.id),
                        country,
                    ]
                )
            ),
        [countries]
    );

    const configuredIds = useMemo(
        () =>
            new Set(
                rows
                    .map(
                        (row) =>
                            row.destinationCountryId
                    )
                    .filter(Boolean)
                    .map(String)
            ),
        [rows]
    );

    const availableCountryOptions = useMemo(
        () =>
            (countries || [])
                .filter(
                    (country) =>
                        !configuredIds.has(
                            String(country.id)
                        )
                )
                .map((country) => ({
                    value: country.id,
                    label: country.ISOCode
                        ? `${country.name} (${country.ISOCode})`
                        : country.name,
                })),
        [
            countries,
            configuredIds,
        ]
    );

    const configuredRows = useMemo(
        () =>
            rows.map((row, index) => {
                const country =
                    countryById.get(
                        String(
                            row.destinationCountryId
                        )
                    );

                return {
                    ...row,
                    index,
                    country,
                };
            }),
        [
            rows,
            countryById,
        ]
    );

    const incompleteRows =
        configuredRows.filter((row) => {
            if (
                !row.destinationCountryId
            ) {
                return true;
            }

            if (
                row.price === "" ||
                row.price === null ||
                row.price === undefined
            ) {
                return true;
            }

            const price =
                Number(row.price);

            return (
                !Number.isFinite(price) ||
                price < 0
            );
        });

    const serviceIncomplete =
        Boolean(service.enabled) &&
        (
            rows.length === 0 ||
            incompleteRows.length > 0
        );

    const setEnabled = (enabled) => {
        setFieldValue(
            `${basePath}.enabled`,
            enabled
        );

        if (!enabled) {
            setSelectedCountryIds([]);
        }
    };

    const addCountries = () => {
        const ids = Array.isArray(
            selectedCountryIds
        )
            ? selectedCountryIds
            : [];

        if (!ids.length) {
            return;
        }

        const nextRows = [
            ...rows,
            ...ids
                .filter(
                    (countryId) =>
                        !configuredIds.has(
                            String(countryId)
                        )
                )
                .map((countryId) => ({
                    destinationCountryId:
                        countryId,

                    // 空字符串要求用户确认价格。
                    // 0 表示明确免费。
                    price: "",

                    description: "",
                })),
        ];

        setFieldValue(
            `${basePath}.countries`,
            nextRows
        );

        setSelectedCountryIds([]);
    };

    const removeCountry = (index) => {
        const nextRows =
            rows.filter(
                (_, rowIndex) =>
                    rowIndex !== index
            );

        setFieldValue(
            `${basePath}.countries`,
            nextRows
        );
    };

    const updateCountryField = (
        index,
        field,
        value
    ) => {
        setFieldValue(
            `${basePath}.countries.${index}.${field}`,
            value
        );
    };

    if (!config) {
        return null;
    }

    return (
        <Card className="mb-4">
            <Card.Header>
                <div
                    className="
                        d-flex
                        flex-column
                        flex-sm-row
                        justify-content-between
                        align-items-sm-start
                        gap-3
                    "
                >
                    <div>
                        <div className="fw-semibold">
                            {config.title}
                        </div>

                        <Form.Text className="text-muted">
                            {config.description}
                        </Form.Text>
                    </div>

                    <Form.Check
                        type="switch"
                        id={`${basePath}.enabled`}
                        label={
                            service.enabled
                                ? "Enabled"
                                : "Disabled"
                        }
                        checked={Boolean(
                            service.enabled
                        )}
                        onChange={(event) =>
                            setEnabled(
                                event.target.checked
                            )
                        }
                        className="flex-shrink-0 mb-0"
                    />
                </div>
            </Card.Header>

            {service.enabled && (
                <Card.Body>
                    <Row
                        className="
                            g-3
                            align-items-end
                            mb-4
                        "
                    >
                        <Col lg={9}>
                            <Form.Group>
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-1">
                                        Add destination countries
                                    </Form.Label>

                                    <HelpTooltip
                                        id={`${serviceKey}-countries`}
                                    >
                                        Select the countries where this
                                        service is available. You may add
                                        several countries at once.
                                    </HelpTooltip>
                                </div>

                                <SmartSearchSelect
                                    currentID={
                                        selectedCountryIds
                                    }
                                    options={
                                        availableCountryOptions
                                    }
                                    onChange={(
                                        selectedOptions
                                    ) =>
                                        setSelectedCountryIds(
                                            Array.isArray(
                                                selectedOptions
                                            )
                                                ? selectedOptions.map(
                                                    (option) =>
                                                        option.value
                                                )
                                                : []
                                        )
                                    }
                                    placeholder="Search and select countries..."
                                    isMulti
                                    closeMenuOnSelect={false}
                                    maxVisibleMultiTags={3}
                                    allowExpandMultiTags
                                />
                            </Form.Group>
                        </Col>

                        <Col lg={3}>
                            <Button
                                type="button"
                                variant="outline-primary"
                                className="w-100"
                                disabled={
                                    !selectedCountryIds.length
                                }
                                onClick={addCountries}
                            >
                                Add selected
                            </Button>
                        </Col>
                    </Row>

                    {rows.length === 0 ? (
                        <Alert
                            variant="light"
                            className="
                                border
                                text-muted
                                mb-0
                            "
                        >
                            No destination countries have been
                            configured for this service.
                        </Alert>
                    ) : (
                        <>
                            <div
                                className="
                                    d-none
                                    d-md-grid
                                    gap-3
                                    border-bottom
                                    pb-2
                                    mb-2
                                    text-muted
                                    small
                                "
                                style={{
                                    gridTemplateColumns:
                                        "minmax(180px, 1fr) minmax(190px, 260px) auto",
                                }}
                            >
                                <div>
                                    Country
                                </div>

                                <div className="d-flex align-items-center">
                                    One-time fee

                                    <HelpTooltip
                                        id={`${serviceKey}-fee`}
                                    >
                                        This amount is charged once per
                                        booking, regardless of the rental
                                        duration. Enter 0 for a free
                                        service.
                                    </HelpTooltip>
                                </div>

                                <div>
                                    Action
                                </div>
                            </div>

                            <div className="d-grid gap-2">
                                {configuredRows.map(
                                    ({
                                        index,
                                        country,
                                        price,
                                    }) => {
                                        const numericPrice =
                                            price === ""
                                                ? null
                                                : Number(price);

                                        const invalidPrice =
                                            price === "" ||
                                            !Number.isFinite(
                                                numericPrice
                                            ) ||
                                            numericPrice < 0;

                                        return (
                                            <div
                                                key={
                                                    rows[index]
                                                        ?.destinationCountryId ||
                                                    index
                                                }
                                                className="
                                                    border
                                                    rounded
                                                    p-3
                                                "
                                            >
                                                <div
                                                    className="
                                                        d-grid
                                                        gap-3
                                                        align-items-center
                                                    "
                                                    style={{
                                                        gridTemplateColumns:
                                                            "minmax(180px, 1fr) minmax(190px, 260px) auto",
                                                    }}
                                                >
                                                    <div>
                                                        <div className="fw-semibold">
                                                            {country?.name ||
                                                                "Unknown country"}
                                                        </div>

                                                        {country?.ISOCode && (
                                                            <div className="text-muted small">
                                                                {
                                                                    country.ISOCode
                                                                }
                                                            </div>
                                                        )}
                                                    </div>

                                                    <Form.Group>
                                                        <InputGroup>
                                                            <InputGroup.Text>
                                                                €
                                                            </InputGroup.Text>

                                                            <Form.Control
                                                                type="number"
                                                                inputMode="decimal"
                                                                min={0}
                                                                step="0.01"
                                                                value={
                                                                    price ?? ""
                                                                }
                                                                isInvalid={
                                                                    submitCount > 0 &&
                                                                    invalidPrice
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    updateCountryField(
                                                                        index,
                                                                        "price",
                                                                        decimalOrEmpty(
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                }
                                                                placeholder="0.00"
                                                            />

                                                            <InputGroup.Text>
                                                                once
                                                            </InputGroup.Text>

                                                            <Form.Control.Feedback type="invalid">
                                                                Enter a valid
                                                                amount of 0 or
                                                                more.
                                                            </Form.Control.Feedback>
                                                        </InputGroup>

                                                        {numericPrice ===
                                                            0 && (
                                                                <Badge
                                                                    bg="success"
                                                                    className="mt-1 fw-normal"
                                                                >
                                                                    Free
                                                                </Badge>
                                                            )}
                                                    </Form.Group>

                                                    <Button
                                                        type="button"
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() =>
                                                            removeCountry(
                                                                index
                                                            )
                                                        }
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        </>
                    )}

                    {submitCount > 0 &&
                        serviceIncomplete && (
                            <Alert
                                variant="danger"
                                className="py-2 mt-3 mb-0"
                            >
                                Add at least one country and
                                enter a valid one-time fee for
                                every configured country.
                            </Alert>
                        )}
                </Card.Body>
            )}
        </Card>
    );
}